/**
 * Translation mapping for reference table values
 * Based on TranslationScheme.json from stripes-smart-components
 */

export const REFERENCE_TABLE_TRANSLATION_MAP = {
  // Material Types
  material_type: {
    appName: 'ui-inventory',
    tableName: 'materialTypes',
    field: 'name',
  },
  // Loan Types
  loan_type: {
    appName: 'ui-inventory',
    tableName: 'loantypes',
    field: 'name',
  },
  // Call Number Types
  call_number_type: {
    appName: 'ui-inventory',
    tableName: 'callNumberTypes',
    field: 'name',
  },
  // Instance Types
  instance_type: {
    appName: 'ui-inventory',
    tableName: 'instanceTypes',
    field: 'name',
  },
  // Instance Statuses
  instance_status: {
    appName: 'ui-inventory',
    tableName: 'instanceStatuses',
    field: 'name',
  },
  // Mode of Issuance
  mode_of_issuance: {
    appName: 'ui-inventory',
    tableName: 'modesOfIssuance',
    field: 'name',
  },
  // Contributor Types
  contributor_type: {
    appName: 'ui-inventory',
    tableName: 'contributorTypes',
    field: 'name',
  },
  // Identifier Types
  identifier_type: {
    appName: 'ui-inventory',
    tableName: 'identifierTypes',
    field: 'name',
  },
  // Classification Types
  classification_type: {
    appName: 'ui-inventory',
    tableName: 'classificationTypes',
    field: 'name',
  },
  // Instance Note Types
  instance_note_type: {
    appName: 'ui-inventory',
    tableName: 'instanceNoteTypes',
    field: 'name',
  },
  // Alternative Title Types
  alternative_title_type: {
    appName: 'ui-inventory',
    tableName: 'alternativeTitleTypes',
    field: 'name',
  },
  // Statistical Codes
  statistical_code: {
    appName: 'ui-inventory',
    tableName: 'statisticalCodes',
    field: 'name',
  },
  // Statistical Code Types
  statistical_code_type: {
    appName: 'ui-inventory',
    tableName: 'statisticalCodeTypes',
    field: 'name',
  },
  // Instance Formats
  instance_format: {
    appName: 'ui-inventory',
    tableName: 'instanceFormats',
    field: 'name',
  },
  // Nature of Content Terms
  nature_of_content_term: {
    appName: 'ui-inventory',
    tableName: 'natureOfContentTerms',
    field: 'name',
  },
  // Holdings Types
  holdings_type: {
    appName: 'ui-inventory',
    tableName: 'holdingsTypes',
    field: 'name',
  },
  // Holdings Note Types
  holdings_note_type: {
    appName: 'ui-inventory',
    tableName: 'holdingsNoteTypes',
    field: 'name',
  },
  // Item Note Types
  item_note_type: {
    appName: 'ui-inventory',
    tableName: 'itemNoteTypes',
    field: 'name',
  },
  // Locations
  location: {
    appName: 'ui-tenant-settings',
    tableName: 'locations',
    field: 'name',
  },
  // Libraries
  loclibrary: {
    appName: 'ui-tenant-settings',
    tableName: 'loclibs',
    field: 'name',
  },
  // Campuses
  loccampus: {
    appName: 'ui-tenant-settings',
    tableName: 'loccamps',
    field: 'name',
  },
  // Institutions
  locinstitution: {
    appName: 'ui-tenant-settings',
    tableName: 'locinsts',
    field: 'name',
  },
};

/**
 * Get translation key for a reference table value
 * @param {string} cubeName - The cube/table name (e.g., 'material_type')
 * @param {string} fieldName - The field name (e.g., 'name')
 * @param {string} value - The actual value (e.g., 'BOOK')
 * @returns {string|null} - Translation key or null if not translatable
 */
export const getReferenceValueTranslationKey = (cubeName, fieldName, value) => {
  const mapping = REFERENCE_TABLE_TRANSLATION_MAP[cubeName];

  if (!mapping || !value) {
    return null;
  }

  if (mapping.field !== fieldName) {
    return null;
  }

  return `${mapping.appName}.${mapping.tableName}.${fieldName}.${value}`;
};

/**
 * Extract cube name from dimension name
 * @param {string} dimensionName - The dimension name from resultSet (e.g., 'cataloging_view.location')
 * @returns {object|null} - Object with cubeName and fieldName or null
 */
export const extractCubeInfoFromDimension = (dimensionName) => {
  if (!dimensionName || typeof dimensionName !== 'string') {
    return null;
  }

  const parts = dimensionName.split('.');
  if (parts.length < 2) {
    return null;
  }

  const fieldPart = parts[parts.length - 1];

  // Handle item location hierarchy fields (item_location_name, item_library_name, etc.)
  if (fieldPart.startsWith('item_')) {
    const itemFieldPart = fieldPart.replace('item_', '');
    if (itemFieldPart === 'location_name') {
      return { cubeName: 'location', fieldName: 'name' };
    }
    if (itemFieldPart === 'library_name') {
      return { cubeName: 'loclibrary', fieldName: 'name' };
    }
    if (itemFieldPart === 'campus_name') {
      return { cubeName: 'loccampus', fieldName: 'name' };
    }
    if (itemFieldPart === 'institution_name') {
      return { cubeName: 'locinstitution', fieldName: 'name' };
    }
  }

  // Check direct match (e.g., 'location')
  for (const cubeName in REFERENCE_TABLE_TRANSLATION_MAP) {
    if (fieldPart === cubeName) {
      const mapping = REFERENCE_TABLE_TRANSLATION_MAP[cubeName];
      return { cubeName, fieldName: mapping.field };
    }
  }

  // Check with _name suffix (e.g., 'location_name' -> 'location')
  if (fieldPart.endsWith('_name')) {
    const cubeNameWithoutSuffix = fieldPart.replace('_name', '');
    if (REFERENCE_TABLE_TRANSLATION_MAP[cubeNameWithoutSuffix]) {
      const mapping = REFERENCE_TABLE_TRANSLATION_MAP[cubeNameWithoutSuffix];
      return { cubeName: cubeNameWithoutSuffix, fieldName: mapping.field };
    }
  }

  return null;
};

/**
 * Translate a reference value using intl
 * @param {object} intl - react-intl intl object
 * @param {string} cubeName - The cube/table name
 * @param {string} fieldName - The field name
 * @param {string} value - The actual value
 * @returns {string} - Translated value or original value if no translation found
 */
export const translateReferenceValue = (intl, cubeName, fieldName, value) => {
  const translationKey = getReferenceValueTranslationKey(
    cubeName,
    fieldName,
    value,
  );

  if (!translationKey) {
    return value;
  }

  // Try to get translation, fallback to original value
  try {
    return intl.formatMessage({
      id: translationKey,
      defaultMessage: value,
    });
  } catch (error) {
    console.warn(`Translation not found for key: ${translationKey}`);
    return value;
  }
};
